/**
 * Backend-driven component registry.
 *
 * Every block in `message_metadata.content_blocks` and every payload
 * delivered via the SSE `block` envelope carries a `ui_component_type`
 * tag sourced from the backend's `UIComponent` enum (see
 * `app/modules/chats/schemas/ui_component.py`). The frontend never
 * dispatches on `block.kind`, `block.tool`, `block.trace_type`, or any
 * other secondary string — it looks the component up here.
 *
 * Adding a new view component is a two-step process:
 *   1. Add the value to `UIComponent` on both BE and FE.
 *   2. Register the React component below.
 *
 * Components that are too tightly coupled to their parent panel for
 * standalone use (TracePanel inner views, AssistantMessage inline
 * blocks, etc.) are introduced incrementally — for those, the panels
 * read the `ui_component_type` tag and pick the right render path
 * locally but still go through the same enum, so the registry stays
 * the single source of truth.
 */

import React from "react";

import CalculationCard from "../EvidencePanel/cards/CalculationCard";
import CommsMessageCard from "../EvidencePanel/cards/CommsMessageCard";
import ExcelChangeCard from "../EvidencePanel/cards/ExcelChangeCard";
import PdfFactCard from "../EvidencePanel/cards/PdfFactCard";
import RecordFieldCard from "../EvidencePanel/cards/RecordFieldCard";
import WebSourceCard from "../EvidencePanel/cards/WebSourceCard";
import {
	EVIDENCE_KIND_TO_UI_COMPONENT,
	UIComponent,
} from "../../types/uiComponent";
import {
	EvidenceKind,
	type EvidenceEvent,
} from "../../types/evidence";

export interface EvidenceRegistryProps {
	event: EvidenceEvent;
	allEvents: EvidenceEvent[];
	chatId?: string | null;
	onUpstreamClick?: (eventId: string) => void;
}

type EvidenceComponent = React.FC<EvidenceRegistryProps>;

/**
 * A `null` entry means "registered but rendered out-of-band" — the
 * component exists in the codebase but is dispatched by a panel-local
 * lookup (TracePanel, AssistantMessage) rather than the standalone
 * registry call. Keeping the entry here documents the BE → FE contract
 * and lets the coverage test assert that every `UIComponent` value is
 * known to the FE.
 */
type EvidenceEntry = EvidenceComponent | null;

const evidenceComponentRegistry: Partial<Record<UIComponent, EvidenceEntry>> = {
	[UIComponent.EVIDENCE_PDF_FACT]: ({ event }) => <PdfFactCard event={event} />,
	[UIComponent.EVIDENCE_WEB_SOURCE]: ({ event }) => (
		<WebSourceCard event={event} />
	),
	[UIComponent.EVIDENCE_EXCEL_CHANGE]: ({ event, chatId }) => (
		<ExcelChangeCard event={event} chatId={chatId} />
	),
	[UIComponent.EVIDENCE_CALCULATION]: ({ event, allEvents, onUpstreamClick }) => (
		<CalculationCard
			event={event}
			allEvents={allEvents}
			onUpstreamClick={onUpstreamClick}
		/>
	),
	[UIComponent.EVIDENCE_COMMS_MESSAGE]: ({ event }) => (
		<CommsMessageCard event={event} />
	),
	[UIComponent.EVIDENCE_RECORD_FIELD]: ({ event }) => (
		<RecordFieldCard event={event} />
	),
	// Internal-only — consumed by the worker, never rendered.
	[UIComponent.INTERNAL_EVIDENCE_METADATA]: null,
};

/**
 * Resolve the `UIComponent` for an `EvidenceEvent`. New payloads carry
 * the discriminator on the wire; older payloads (pre-migration) fall
 * back to the canonical `kind → UIComponent` mapping mirrored from the
 * backend's `EVIDENCE_KIND_TO_UI_COMPONENT`.
 */
export const resolveEvidenceUIComponent = (
	event: EvidenceEvent,
): UIComponent | null => {
	if (event.ui_component_type) return event.ui_component_type as UIComponent;
	const kindKey = event.kind as EvidenceKind;
	return EVIDENCE_KIND_TO_UI_COMPONENT[kindKey] ?? null;
};

/**
 * Render an evidence event through the registry. Returns `null` when
 * the resolved component is internal-only or unmapped — callers
 * decide how to surface that (typically: skip the row).
 */
export const renderEvidenceEvent = (
	props: EvidenceRegistryProps,
): React.ReactNode => {
	const ui = resolveEvidenceUIComponent(props.event);
	if (!ui) return null;
	const entry = evidenceComponentRegistry[ui];
	if (!entry) return null;
	const Component = entry;
	return <Component {...props} />;
};

/** Test hook — exposes the registry shape without leaking it. */
export const __evidenceRegistryForTests = evidenceComponentRegistry;
